import { StopOutlined, SwapOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, InputNumber, Radio, Select, Space } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';

import type { CabinClass, TripType } from '../../../src/types.ts';
import type { AirportOption } from '../lib/airports.ts';
import { MONTHS } from '../lib/format.ts';
import { AirportSelect } from './AirportSelect.tsx';

export interface SearchFormValues {
  type: TripType;
  origin: AirportOption | null;
  destination: AirportOption | null;
  month: Dayjs;
  stayMin: number;
  stayMax: number;
  /** 1-12, or null for "same month". */
  returnMonth: number | null;
  adults: number;
  children: number;
  infants: number;
  cabin: CabinClass;
}

interface Props {
  form: ReturnType<typeof Form.useForm<SearchFormValues>>[0];
  submitting: boolean;
  onSubmit: (values: SearchFormValues) => void;
  onRequiresLogin: (reason: string) => void;
  /** True while a search is running: Search is disabled and Cancel takes over. */
  running?: boolean;
  onCancel?: () => void;
  cancelling?: boolean;
  /** Airports this account has searched before, offered before anything is typed. */
  recentOrigins?: AirportOption[];
  recentDestinations?: AirportOption[];
}

export const CABIN_LABELS: Record<CabinClass, string> = {
  ECONOMY: 'Economy',
  PREMIUM_ECONOMY: 'Premium Economy',
  BUSINESS: 'Business',
  FIRST: 'First',
};

export function defaultFormValues(): SearchFormValues {
  return {
    type: 'ONEWAY',
    origin: null,
    destination: null,
    month: dayjs().add(1, 'month').startOf('month'),
    stayMin: 7,
    stayMax: 7,
    returnMonth: null,
    adults: 1,
    children: 0,
    infants: 0,
    cabin: 'ECONOMY',
  };
}

export function SearchForm({
  form,
  submitting,
  onSubmit,
  onRequiresLogin,
  running = false,
  onCancel,
  cancelling = false,
  recentOrigins = [],
  recentDestinations = [],
}: Props) {
  const type = Form.useWatch('type', form);

  const swap = () => {
    const { origin, destination } = form.getFieldsValue(['origin', 'destination']);
    form.setFieldsValue({ origin: destination ?? null, destination: origin ?? null });
  };

  return (
    <Form<SearchFormValues>
      form={form}
      layout="vertical"
      initialValues={defaultFormValues()}
      onFinish={onSubmit}
      requiredMark={false}
      autoComplete="off"
    >
      <Form.Item name="type" style={{ marginBottom: 16 }}>
        <Radio.Group optionType="button" buttonStyle="solid" block>
          <Radio.Button value="ONEWAY">One way</Radio.Button>
          <Radio.Button value="ROUNDTRIP">Round trip</Radio.Button>
          <Radio.Button value="MULTI" disabled title="Not supported in this tool">
            Multi
          </Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        label="From"
        name="origin"
        rules={[{ required: true, message: 'Pick a departure airport' }]}
        style={{ marginBottom: 12 }}
      >
        <AirportSelect id="origin" onRequiresLogin={onRequiresLogin} recent={recentOrigins} />
      </Form.Item>

      <div className="swap-row">
        <Button type="text" size="small" icon={<SwapOutlined rotate={90} />} onClick={swap} aria-label="Swap origin and destination">
          Swap
        </Button>
      </div>

      <Form.Item label="To" name="destination" rules={[{ required: true, message: 'Pick a destination airport' }]}>
        <AirportSelect id="destination" onRequiresLogin={onRequiresLogin} recent={recentDestinations} />
      </Form.Item>

      <Form.Item label="Outbound month" name="month" rules={[{ required: true }]}>
        <DatePicker picker="month" allowClear={false} format="MMMM YYYY" style={{ width: '100%' }} />
      </Form.Item>

      {type === 'ROUNDTRIP' && (
        <>
          <Space.Compact block style={{ marginBottom: 0 }}>
            <Form.Item label="Min stay (days)" name="stayMin" style={{ flex: 1 }} rules={[{ required: true }]}>
              <InputNumber min={1} max={60} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item label="Max stay (days)" name="stayMax" style={{ flex: 1 }} rules={[{ required: true }]}>
              <InputNumber min={1} max={60} style={{ width: '100%' }} />
            </Form.Item>
          </Space.Compact>
          <Form.Item label="Return month" name="returnMonth" tooltip="Leave on 'Same month' to add the stay length to each outbound day.">
            <Select
              options={[{ value: null, label: 'Same month' }, ...MONTHS.map((m, i) => ({ value: i + 1, label: m }))]}
            />
          </Form.Item>
        </>
      )}

      <Space.Compact block>
        <Form.Item label="Adults" name="adults" style={{ flex: 1 }} rules={[{ required: true }]}>
          <InputNumber min={1} max={9} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Children" name="children" style={{ flex: 1 }} tooltip="2–11 years">
          <InputNumber min={0} max={8} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Infants" name="infants" style={{ flex: 1 }} tooltip="Under 2, on a lap">
          <InputNumber min={0} max={4} style={{ width: '100%' }} />
        </Form.Item>
      </Space.Compact>
      <Form.Item label="Cabin" name="cabin">
        <Select options={(Object.keys(CABIN_LABELS) as CabinClass[]).map((c) => ({ value: c, label: CABIN_LABELS[c] }))} />
      </Form.Item>

      <Form.Item style={{ marginBottom: 0 }}>
        {running ? (
          <Button danger block size="large" loading={cancelling} onClick={onCancel} icon={<StopOutlined />}>
            Cancel search
          </Button>
        ) : (
          <Button type="primary" htmlType="submit" block size="large" loading={submitting}>
            Search
          </Button>
        )}
      </Form.Item>
    </Form>
  );
}
